import type React from "react";
import { Link } from "react-router";

import type { AdminTicketDetailedListItemDto, PortfolioAdminTicketDto } from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import type { IconComponent } from "@/components/Icon/Icon";
import { Building01Icon } from "@/components/Icons/Building01Icon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { MessageCircle01Icon } from "@/components/Icons/MessageCircle01Icon";
import { User01Icon } from "@/components/Icons/User01Icon";
import { getRemsStatus } from "@/components/Ticket/helpers";
import { RemsStatus } from "@/components/Ticket/RemsStatus";
import { TicketStatus } from "@/components/Ticket/TicketStatus";
import { User } from "@/components/User/User";
import { Badge } from "@/design-system/Badge";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { RatingBadge } from "@/design-system/RatingBadge";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { formatAddress } from "@/helpers/address";
import { daysBetween } from "@/helpers/date";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

function getVisibilityOptionIcon(visibility: string): IconComponent {
  switch (visibility) {
    case "private":
      return <User01Icon size={16} />;
    case "sharedWithBuilding":
      return <Building01Icon size={16} />;
    case "sharedWithCommunity":
    default:
      return <Building04Icon size={16} />;
  }
}

interface TicketCardProps {
  ticket: AdminTicketDetailedListItemDto | PortfolioAdminTicketDto;
  menuActions?: ContextMenuItem[];
  showRating?: boolean;
  showOpenedSince?: boolean;
  shouldNavigate?: boolean;
  onClick?: () => void;
}

export function TicketCard({
  ticket,
  menuActions,
  showRating,
  showOpenedSince,
  shouldNavigate = true,
  onClick,
}: TicketCardProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const { t, i18n } = useTranslation();
  const slug = useSlug();
  const rems = getRemsStatus(ticket);

  const ticketDetailsRoute = routes.tickets.details({
    slug,
    id: ticket.id,
  });

  return (
    <Card as="article" interactive>
      {shouldNavigate ? (
        <Link
          className="absolute inset-0 z-content rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
          to={ticketDetailsRoute}
          aria-label={ticket.title}
        />
      ) : (
        <button
          className="absolute inset-0 z-content rounded-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
          type="button"
          aria-label={ticket.title}
          onClick={onClick}
        />
      )}

      <section className="pointer-events-none relative z-content flex w-full flex-col gap-space-sm">
        <Inline gap="sm">
          <TicketStatus labelColor={ticket.status.color} description={ticket.status.name} variant="filled" />
          <span className="w-full max-w-measure-compact">
            <Badge>{ticket.assignee?.fullName || t("page.tickets.table.content.unassigned")}</Badge>
          </span>
          {ticket.visibility !== "private" && (
            <span className="ml-auto">
              <Badge icon={getVisibilityOptionIcon(ticket.visibility)} size="md" variant="info">
                {t("page.tickets.details.info.type.collective")}
              </Badge>
            </span>
          )}
        </Inline>

        {/* Header */}
        <Spread space="sm" align="center">
          <User
            user={ticket.user}
            renderSubtitle={
              "company" in ticket || "address" in ticket
                ? () => ticket.company?.name || (ticket.address ? formatAddress(ticket.address) : "-")
                : undefined
            }
            shouldNavigate={false}
          />

          {menuActions && (
            <span className="pointer-events-auto relative z-raised">
              <ContextMenu items={menuActions} />
            </span>
          )}
        </Spread>

        <Spread space="sm" align="start">
          <span className="flex items-center gap-space-xs text-left text-body-emphasis">{ticket.title}</span>
        </Spread>
      </section>

      {rems && <RemsStatus rems={rems} />}

      <footer className="pointer-events-none relative z-content flex w-full items-end justify-between">
        <Stack gap="sm">
          {showOpenedSince && (
            <span className="text-caption text-text-secondary">
              {t("page.tickets.table.content.created-at.mobile-prefix")}{" "}
              {t("page.tickets.table.content.created-at", {
                count: daysBetween(ticket.createdAt, clockDate),
              })}
            </span>
          )}
          <span className="text-left text-caption text-text-secondary">
            {t("page.tickets.table.content.title", {
              when: formatDate(i18n, "datetimeShort", ticket.lastActivityAt),
              who: ticket.lastActivityBy?.fullName || ticket.user.fullName,
            })}
          </span>
        </Stack>
        {showRating && !!ticket.rating && <RatingBadge rating={ticket.rating} />}
        <span className="ml-auto flex w-fit items-center gap-space-xs px-space-sm py-space-xs">
          <MessageCircle01Icon />
          <span className="text-body-emphasis">{ticket.activityCount}</span>
        </span>
      </footer>
    </Card>
  );
}
